import { RootState } from "../../app";
import { createSelector } from "@reduxjs/toolkit";
import {
  annotationDoneStatuses,
  doneStatuses,
  Status,
} from "../../common/status/status";
import { DocumentSetData } from "../interfaces/documentSet";
import { User } from "../../user";
import { selectIsShowingSnapshot } from "../../annotation/selectors/annotationReducerSelector";

const getUser = (state: RootState) => state.userReducer.user;
const getActiveDocumentSet = (state: RootState) =>
  state.documentSetsReducer.activeDocumentSet;
const isAnnotationMode = (_state: RootState) => false;
const getEditAfterExportMode = (state: RootState) =>
  state.appReducer.editAfterExportMode;

const getPdf = (state: RootState) => state.documentReducer.pdf;

export const isDocumentSetDisabled = (
  activeDocumentSet: DocumentSetData | undefined,
  user: User | null,
  isAnnotationMode: boolean,
  pdfPagesLength?: number,
  editAfterExportMode?: boolean,
): boolean => {
  if (!activeDocumentSet) {
    return true;
  }

  // While re-annotating an exported/approved document, the side controls become
  // editable again — bypass the normal "done" status disable.
  if (
    editAfterExportMode &&
    (activeDocumentSet.status === Status.Exported ||
      activeDocumentSet.status === Status.Approved)
  ) {
    return false;
  }

  if (annotationDoneStatuses.includes(activeDocumentSet.status)) {
    return true;
  }

  if (isAnnotationMode) {
    return false;
  }

  if (activeDocumentSet.status === Status.ToDo) {
    return false;
  }

  if (
    activeDocumentSet.assignee &&
    user?.id !== activeDocumentSet.assignee.id
  ) {
    return true;
  }

  if (activeDocumentSet.status === Status.Error && pdfPagesLength) {
    return false;
  }

  return (
    doneStatuses.includes(activeDocumentSet.status) &&
    activeDocumentSet.status !== Status.Error
  );
};

const documentSetDisabledSelector = createSelector(
  [
    getUser,
    getActiveDocumentSet,
    isAnnotationMode,
    getPdf,
    getEditAfterExportMode,
    selectIsShowingSnapshot,
  ],
  (
    user,
    activeDocumentSet,
    isAnnotationMode,
    pdf,
    editAfterExportMode,
    isShowingSnapshot,
  ) => {
    // The exported annotations are a past revision, not an editing target.
    if (isShowingSnapshot) {
      return true;
    }
    return isDocumentSetDisabled(
      activeDocumentSet,
      user,
      isAnnotationMode,
      pdf?.pages.length,
      editAfterExportMode,
    );
  },
);

export default documentSetDisabledSelector;
